import React, { useEffect, useRef, useState } from 'react';
import useApiLoaderHelper from '../../../utils/useApiLoaderHelper';
import {
    CheckmarkIcon,
    DetailSection,
    ExpandButton,
    HeaderLabel,
    HeaderRow,
    LoaderItemRow,
    Overlay,
    PanelContainer,
    ProgressFraction,
    SmallSpinner,
} from './LoadingPanel.styles';

const MAX_ITEMS = 8;
const HIDE_DELAY_MS = 500;

const LoadingPanel: React.FC = () => {
    const { getNonMutatingLoaders, clearCompletedLoaders } = useApiLoaderHelper();
    const loaders = getNonMutatingLoaders().filter((l) => l.message);
    const [isExpanded, setIsExpanded] = useState(false);

    const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const clearCompletedRef = useRef(clearCompletedLoaders);

    // Keep ref up-to-date so the timer callback always calls the latest version
    useEffect(() => {
        clearCompletedRef.current = clearCompletedLoaders;
    });

    // Stable key that only changes when loader state actually changes
    const loadersKey = loaders.map((l) => `${l.requestId}:${l.status}`).join(',');

    useEffect(() => {
        const hasActiveLoaders = loaders.some((l) => l.status === 'loading');

        if (hasActiveLoaders) {
            // A new loader started — cancel any pending hide timer
            if (hideTimerRef.current) {
                clearTimeout(hideTimerRef.current);
                hideTimerRef.current = null;
            }
        } else if (loaders.length > 0 && !hideTimerRef.current) {
            // All loaders completed — start debounced cleanup timer
            hideTimerRef.current = setTimeout(() => {
                clearCompletedRef.current();
                hideTimerRef.current = null;
            }, HIDE_DELAY_MS);
        }

        return () => {
            if (hideTimerRef.current) {
                clearTimeout(hideTimerRef.current);
                hideTimerRef.current = null;
            }
        };
    }, [loadersKey]); // eslint-disable-line react-hooks/exhaustive-deps

    // Visibility is derived entirely from loader state — no separate visibility state needed.
    // The panel unmounts when loaders are cleaned up (length === 0).
    if (loaders.length === 0) return null;

    const totalCount = loaders.length;
    const completedCount = loaders.filter((l) => l.status === 'completed').length;
    const allDone = completedCount === totalCount;

    // Sort: active loaders first so they get priority in the visible slots
    const sortedLoaders = [...loaders].sort((a, b) => {
        if (a.status === 'loading' && b.status !== 'loading') return -1;
        if (a.status !== 'loading' && b.status === 'loading') return 1;
        return 0;
    });

    const displayedLoaders = sortedLoaders.slice(0, MAX_ITEMS);
    const overflowLoaders = sortedLoaders.slice(MAX_ITEMS);
    const overflowCount = overflowLoaders.length;
    const overflowHasActive = overflowLoaders.some((l) => l.status === 'loading');

    return (
        <Overlay>
            <PanelContainer>
                <HeaderRow>
                    {allDone ? <CheckmarkIcon>✓</CheckmarkIcon> : <SmallSpinner />}
                    <HeaderLabel>Loading data...</HeaderLabel>
                    <ProgressFraction>
                        {completedCount}/{totalCount} loaded
                    </ProgressFraction>
                    <ExpandButton onClick={() => setIsExpanded((prev) => !prev)} aria-label={isExpanded ? 'Collapse' : 'Expand'}>
                        {isExpanded ? '▲' : '▼'}
                    </ExpandButton>
                </HeaderRow>
                {isExpanded && (
                    <DetailSection>
                        {displayedLoaders.map((loader, idx) => (
                            <LoaderItemRow key={loader.requestId ?? idx}>
                                {loader.status === 'completed' ? <CheckmarkIcon>✓</CheckmarkIcon> : <SmallSpinner />}
                                <span>{loader.message}</span>
                            </LoaderItemRow>
                        ))}
                        {overflowCount > 0 && (
                            <LoaderItemRow>
                                {overflowHasActive ? <SmallSpinner /> : <CheckmarkIcon>✓</CheckmarkIcon>}
                                <span>+ {overflowCount} more...</span>
                            </LoaderItemRow>
                        )}
                    </DetailSection>
                )}
            </PanelContainer>
        </Overlay>
    );
};

export default LoadingPanel;
